import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Services } from "assets/SideBarIcons/services.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { useEffect, useRef, useState } from "react";
import { GetServiceByIdResponse } from "subModule/src/services/service/interface";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import {
  ServiceProfileService,
  TaxProcedureProfileService,
} from "subModule/src/core/services";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
import CommonCheckbox from "common/CheckBox/Checkbox";
import ServiceTabs from "./ServiceTabs";
import { useGetServiceGroupDropdownService } from "subModule/src/services/servicegroup/useServiceGroup";
import FormDialog from "common/Modal/Modal";
import ServiceGroupModal from "pages/ServiceGroup/ServiceGroupModal";
import { ReactComponent as ModalBtn } from "assets/Modal/ModalBtn.svg";
import { useGetTaxProcedureDropdownService } from "subModule/src/services/taxProcedure/useTaxProcedure";
import Currency from "common/Others/Currency";
import { GetDefaultTaxProcedureResponse } from "subModule/src/services/taxProcedure/interface";
import { useGetItemGroupDropdownService } from "subModule/src/services/itemgroup/useItemGroup";
import { useGetChecklistsDropdownService } from "subModule/src/services/checklist/useChecklist";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import InfoIcon from "@mui/icons-material/Info";
interface FormData {
  [key: string]: any;
}
interface ServiceProps {
  setAddNew?: any;
  serviceRefetch?: any;
}

const formatBilledTimeValue = (value: number): number => {
  if (Number.isNaN(value)) return value;
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? rounded : parseFloat(rounded.toFixed(2));
};

export default function ServicesModal(props: ServiceProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      applyToAllStations: true,
      itemLimit: null,
      type: { value: 101, label: t("internal") },
      attributes: {
        serviceTimeUnit: { value: 2, label: t("minutes") },
      },
    },
  });
  const [loading, setLoading] = useState<boolean>(
    params?.id && !props?.setAddNew ? true : false,
  );
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id && !props?.setAddNew ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "Items",
    moduleLabelKey: "services",
    moduleHref: "/services",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id && !props?.setAddNew,
  });
  const [addNew, setAddNew] = useState<boolean>(false);
  const [submittedFormData, setSubmittedFormData] = useState<FormData | null>(
    null,
  );
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [prevFormData, setPrevFormData] = useState<any>({});
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedServiceTitlePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phServiceTitleAr") : t("phServiceTitleEn");
  const loggedInUserCurrency = loggedInUserData?.business?.currency;
  const defaultPriceList = loggedInUserData?.business?.priceListTitle;
  const { data: itemGroupsData, refetch: itemGroupsRefetch } =
    useGetItemGroupDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: taxProceduresData, refetch: taxProceduresRefetch } =
    useGetTaxProcedureDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: checklistsData, refetch: checklistsRefetch } =
    useGetChecklistsDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: serviceGroupsData, refetch: serviceGroupsRefetch } =
    useGetServiceGroupDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const itemGroups = (itemGroupsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; title?: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const taxProcedures = (taxProceduresData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string; percentageValue?: number }) => ({
      value: item.id,
      label: item.title,
      percentageValue: item.percentageValue,
    }),
  );
  const checklists = (checklistsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; title?: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const serviceGroups = (serviceGroupsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; title?: string; default?: boolean }) => ({
      value: item.id,
      label: item.title,
      isDisabled: watch("type")?.value === 101 && item.default === true,
    }),
  );
  const groupOptions = [
    { value: 1, label: t("groups") },
    { value: 2, label: t("open") },
    { value: 3, label: t("selected") },
    { value: 4, label: t("bundled") },
  ];
  const pricingScopes = [
    { value: 2, label: t("Part & Service") },
    { value: 1, label: t("Service only") },
  ];
  const pricingTypes = [
    { value: 1, label: t("fixedPrice") },
    { value: 2, label: t("standardLaborCost") },
  ];
  const serviceTimeUnitOptions = [
    { value: 1, label: t("hours") },
    { value: 2, label: t("minutes") },
  ];
  const categories = [
    { value: 1, label: t("internal") },
    { value: 4, label: t("external") },
  ];
  const currencyOptions = Currency.map((item) => ({
    value: item.value,
    label: item.title,
    symbol: item.symbol,
  }));
  const currencyLabel =
    currencyOptions.find((option: any) => option.value === loggedInUserCurrency)
      ?.symbol || "";
  useEffect(() => {
    itemGroupsRefetch();
    taxProceduresRefetch();
    serviceGroupsRefetch();
    checklistsRefetch();
    if (!params?.id) {
      getDefaultTaxForPrice();
      getDefaultTaxForCost();
    }
  }, []);
  useEffect(() => {
    if (
      params?.id &&
      !props?.setAddNew &&
      itemGroupsData &&
      checklistsData &&
      serviceGroupsData &&
      taxProceduresData
    ) {
      if (loading) getByID();
    }
  }, [
    params,
    itemGroupsData,
    checklistsData,
    serviceGroupsData,
    taxProceduresData,
  ]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getDefaultTaxForPrice = () => {
    TaxProcedureProfileService.getActiveDefaultTaxProcedure(3)
      .then((res: GetDefaultTaxProcedureResponse) => {
        if (res.success) {
          setValue("taxProcedureId", {
            value: res.data?.id,
            label: res.data?.title,
            percentageValue: res.data?.percentageValue,
          });
          setValue(
            "taxValue",
            (
              watch("price") *
              ((res?.data?.percentageValue || 0) / 100)
            )?.toFixed(2),
          );
          setValue(
            "priceWithTax",
            parseFloat(watch("price")) +
              watch("price") * ((res?.data?.percentageValue || 0) / 100),
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      })
      .finally(() => setLoading(false));
  };
  const getDefaultTaxForCost = () => {
    TaxProcedureProfileService.getActiveDefaultTaxProcedure(3)
      .then((res: GetDefaultTaxProcedureResponse) => {
        if (res.success) {
          setValue("costTaxProcedureId", {
            value: res.data?.id,
            label: res.data?.title,
            percentageValue: res.data?.percentageValue,
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      })
      .finally(() => setLoading(false));
  };
  const getByID = () => {
    ServiceProfileService.getServiceById(+(params as any)?.id)
      .then((res: GetServiceByIdResponse) => {
        if (res.success) {
          const serviceTimeUnit = res.data?.attributes?.serviceTimeUnit;
          const serviceTimeUnitValue =
            (serviceTimeUnit as any)?.value ?? serviceTimeUnit ?? 2;
          const serviceTimeUnitField = {
            value: serviceTimeUnitValue,
            label:
              serviceTimeUnitOptions.find(
                (v) => v.value === serviceTimeUnitValue,
              )?.label || "",
          };
          const rawDisplayServiceTime =
            serviceTimeUnitValue === 1 && res.data?.serviceTime != null
              ? res.data.serviceTime / 60
              : res.data?.serviceTime;
          const displayServiceTime =
            rawDisplayServiceTime != null &&
            !Number.isNaN(+rawDisplayServiceTime)
              ? formatBilledTimeValue(+rawDisplayServiceTime)
              : rawDisplayServiceTime;
          reset({
            ...(res?.data || {}),
            itemLimit: res?.data?.itemLimit || null,
            itemGroup: res.data?.itemGroup
              ? {
                  value: res.data?.itemGroup,
                  label:
                    groupOptions.find((v) => v.value === res.data?.itemGroup)
                      ?.label || res.data?.itemGroup,
                }
              : null,
            itemGroupId: res.data?.itemGroupId
              ? {
                  value: res.data?.itemGroupId,
                  label: res.data?.itemGroupTitle || res.data?.itemGroupId,
                }
              : null,
            taxProcedureId: res.data?.taxProcedureId
              ? {
                  value: res.data?.taxProcedureId,
                  label:
                    res?.data?.taxProcedureTitle || res.data?.taxProcedureId,
                  percentageValue: res?.data?.taxPercentageValue || 0,
                }
              : null,
            costTaxProcedureId: res.data?.costTaxProcedureId
              ? {
                  value: res.data?.costTaxProcedureId,
                  label:
                    res?.data?.costTaxProcedureTitle ||
                    res.data?.costTaxProcedureId,
                  percentageValue: res?.data?.costTaxPercentageValue || 0,
                }
              : null,
            pricingScope: res.data?.pricingScope
              ? {
                  value: res.data?.pricingScope,
                  label: pricingScopes.find(
                    (v) => v.value === res.data?.pricingScope,
                  )?.label,
                }
              : null,
            pricingType: res.data?.pricingType
              ? {
                  value: res.data?.pricingType,
                  label: pricingTypes.find(
                    (v) => v.value === res.data?.pricingType,
                  )?.label,
                }
              : null,
            serviceGroupId: res.data?.serviceGroupId
              ? {
                  value: res.data?.serviceGroupId,
                  label:
                    res?.data?.serviceGroupTitle || res.data?.serviceGroupId,
                }
              : null,
            checklistId: res.data?.checklistId
              ? {
                  value: res.data?.checklistId,
                  label: res?.data?.checklistTitle || res.data?.checklistId,
                }
              : null,
            taxValue: (
              (res?.data.price || 0) *
              ((res?.data?.taxPercentageValue || 0) / 100)
            )?.toFixed(2),
            costtaxValue: (
              (res?.data.cost || 0) *
              ((res?.data?.costTaxPercentageValue || 0) / 100)
            )?.toFixed(2),
            type: res?.data?.type
              ? {
                  value: res?.data?.type,
                  label:
                    categories.find((v) => v.value === res?.data.type)?.label ||
                    res.data.type,
                }
              : null,
            attributes: {
              ...(res.data?.attributes || {}),
              serviceTimeUnit: serviceTimeUnitField,
            },
            serviceTime: displayServiceTime,
          });
          setPrevFormData(res?.data || {});
          setSubmittedFormData({
            ...(res?.data || {}),
            itemLimit: res?.data?.itemLimit || null,
            itemGroup: res.data?.itemGroup
              ? {
                  value: res.data?.itemGroup,
                  label:
                    groupOptions.find((v) => v.value === res.data?.itemGroup)
                      ?.label || res.data?.itemGroup,
                }
              : null,
            itemGroupId: res.data?.itemGroupId
              ? {
                  value: res.data?.itemGroupId,
                  label: res.data?.itemGroupTitle || res.data?.itemGroupId,
                }
              : null,
            taxProcedureId: res.data?.taxProcedureId
              ? {
                  value: res.data?.taxProcedureId,
                  label:
                    res?.data?.taxProcedureTitle || res.data?.taxProcedureId,
                  percentageValue: res?.data?.taxPercentageValue || 0,
                }
              : null,
            costTaxProcedureId: res.data?.costTaxProcedureId
              ? {
                  value: res.data?.costTaxProcedureId,
                  label:
                    res?.data?.costTaxProcedureTitle ||
                    res.data?.costTaxProcedureId,
                  percentageValue: res?.data?.costTaxPercentageValue || 0,
                }
              : null,
            pricingScope: res.data?.pricingScope
              ? {
                  value: res.data?.pricingScope,
                  label: pricingScopes.find(
                    (v) => v.value === res.data?.pricingScope,
                  )?.label,
                }
              : null,
            pricingType: res.data?.pricingType
              ? {
                  value: res.data?.pricingType,
                  label: pricingTypes.find(
                    (v) => v.value === res.data?.pricingType,
                  )?.label,
                }
              : null,
            serviceGroupId: res.data?.serviceGroupId
              ? {
                  value: res.data?.serviceGroupId,
                  label:
                    res?.data?.serviceGroupTitle || res.data?.serviceGroupId,
                }
              : null,
            checklistId: res.data?.checklistId
              ? {
                  value: res.data?.checklistId,
                  label: res?.data?.checklistTitle || res.data?.checklistId,
                }
              : null,
            taxValue: (
              (res?.data.price || 0) *
              ((res?.data?.taxPercentageValue || 0) / 100)
            )?.toFixed(2),
            costtaxValue: (
              (res?.data.cost || 0) *
              ((res?.data?.costTaxPercentageValue || 0) / 100)
            )?.toFixed(2),
            type: res?.data?.type
              ? {
                  value: res?.data?.type,
                  label:
                    categories.find((v) => v.value === res?.data.type)?.label ||
                    res.data.type,
                }
              : null,
            attributes: {
              ...(res.data?.attributes || {}),
              serviceTimeUnit: serviceTimeUnitField,
            },
            serviceTime: displayServiceTime,
          });
          captureLabel(res?.data?.title);
          setLoading(false);
          setValue(
            "priceWithTax",
            parseFloat(watch("price")) + parseFloat(watch("taxValue")),
          );
          setValue(
            "costWithTax",
            parseFloat(watch("cost")) + parseFloat(watch("costtaxValue")),
          );
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    delete data.taxValue;
    delete data.priceWithTax;
    delete data.tax;
    delete data.costtaxValue;
    delete data.costtax;
    delete data.costWithTax;
    if (data?.type?.value === 101) {
      delete data.taxableCost;
      delete data.costTaxProcedureId;
      delete data.cost;
    }
    if (params?.id && !props?.setAddNew) {
      ServiceProfileService.updateService(+params?.id, {
        ...data,
        itemGroupId: data?.itemGroupId?.value || 0,
        taxProcedureId:
          data?.taxable && data?.taxProcedureId?.value
            ? data?.taxProcedureId?.value
            : 0,
        pricingScope: data?.pricingScope?.value || 0,
        pricingType: data?.pricingType?.value || 0,
        itemGroup: data?.itemGroup?.value || 0,
        serviceGroupId: data?.serviceGroupId?.value || 0,
        checklistId: data?.checklistId?.value || 0,
        itemLimit: data?.itemLimit ? +data?.itemLimit : null,
        serviceTime:
          ((data?.attributes?.serviceTimeUnit as any)?.value ??
            data?.attributes?.serviceTimeUnit ??
            2) === 1
            ? +data?.serviceTime * 60
            : +data?.serviceTime,
        attributes: {
          ...(data?.attributes || {}),
          serviceTimeUnit:
            (data?.attributes?.serviceTimeUnit as any)?.value ??
            data?.attributes?.serviceTimeUnit ??
            2,
        },
        type: data?.type?.value || null,
        costTaxProcedureId:
          data?.taxableCost && data?.costTaxProcedureId?.value
            ? data?.costTaxProcedureId?.value
            : null,
      })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            setSubmittedFormData(data);
            getByID();
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("service") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      ServiceProfileService.createService({
        ...data,
        itemGroupId: data?.itemGroupId?.value || 0,
        taxProcedureId:
          data?.taxable && data?.taxProcedureId?.value
            ? data?.taxProcedureId?.value
            : 0,
        pricingScope: data?.pricingScope?.value || 0,
        pricingType: data?.pricingType?.value || 0,
        itemGroup: data?.itemGroup?.value || 0,
        serviceGroupId: data?.serviceGroupId?.value || 0,
        checklistId: data?.checklistId?.value || 0,
        itemLimit: data?.itemLimit ? +data?.itemLimit : null,
        serviceTime:
          ((data?.attributes?.serviceTimeUnit as any)?.value ??
            data?.attributes?.serviceTimeUnit ??
            2) === 1
            ? +data?.serviceTime * 60
            : +data?.serviceTime,
        attributes: {
          ...(data?.attributes || {}),
          serviceTimeUnit:
            (data?.attributes?.serviceTimeUnit as any)?.value ??
            data?.attributes?.serviceTimeUnit ??
            2,
        },
        type: data?.type?.value || null,
        costTaxProcedureId:
          data?.taxableCost && data?.costTaxProcedureId?.value
            ? data?.costTaxProcedureId?.value
            : null,
      })
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("service") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            if (props?.setAddNew) {
              props?.setAddNew("");
              props?.serviceRefetch();
            } else navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "itemGroups")
        itemGroupsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "serviceGroups")
        serviceGroupsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "taxProcedures" || typeName === "costtaxProcedures")
        taxProceduresRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "checklists")
        checklistsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      {props?.setAddNew ? null : (
        <ComponentHeaderTwo
          showBtn={true}
          showSaveBtn={true}
          onClick={() => navigate("/services")}
          onSubmit={handleSubmit(onSubmit)}
          heading={
            params?.id && !props?.setAddNew ? t("services") : t("addService")
          }
          icon={<Services height={28} width={28} />}
          btnText={"back"}
          btnType={"back"}
          isBtnEnabled={IsBtnEnabled}
          showCreateBtn={checkPrivileges("ROLE_SERVICE_CREATE")}
          breadcrumbItems={breadcrumbItems}
        />
      )}
      <div className={`${params?.id && !props?.setAddNew ? "flex" : ""}`}>
        {params?.id && !props?.setAddNew ? (
          <ServiceTabs
            params={params}
            selectedIndex={0}
            formData={submittedFormData}
          />
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={
              params?.id && !props?.setAddNew ? t("services") : t("addService")
            }
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id && !props?.setAddNew ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            entityPopup={props?.setAddNew}
            content={
              <>
                <Grid
                  container
                  component="form"
                  id="form"
                  spacing={2}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="type"
                        control={control}
                        defaultValue={null}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("category") + " *"}
                            description={
                              <Box component="ul" sx={{ margin: 0 }}>
                                <li>
                                  <strong>{t("internal")}:</strong>{" "}
                                  {t("serviceCategoryInfoInternal")}
                                </li>
                                <li>
                                  <strong>{t("external")}:</strong>{" "}
                                  {t("serviceCategoryInfoExternal")}
                                </li>
                              </Box>
                            }
                            {...field}
                            options={categories}
                            defaultPlaceholder="phSelectServiceCategory"
                            onChange={(selectedOption: any) => {
                              field.onChange(selectedOption);
                              if (selectedOption?.value === 102)
                                setValue("serviceGroupId", null);
                            }}
                            error={errors.type?.message as string}
                            isDisabled={
                              (params as any)?.id && prevFormData?.type
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("serviceCode", {
                          validate: {
                            required: (value) =>
                              value?.trim() !== "" ||
                              t("This field is required."),
                          },
                        })}
                        label={t("serviceCode") + " *"}
                        placeholder={t("phServiceCode")}
                        title={t("serviceCode")}
                        fullWidth
                        error={!!errors.serviceCode}
                        helperText={errors.serviceCode?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("title", {
                          validate: {
                            required: (value) =>
                              value?.trim() !== "" ||
                              t("This field is required."),
                          },
                        })}
                        label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                        placeholder={localizedServiceTitlePlaceholder(
                          primaryLanguage,
                        )}
                        title={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                          .replace(/\s+/g, " ")
                          .trim()}
                        fullWidth
                        error={!!errors.title}
                        helperText={errors.title?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("secondaryTitle")}
                        label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                        placeholder={localizedServiceTitlePlaceholder(
                          secondaryLanguage,
                        )}
                        title={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                          .replace(/\s+/g, " ")
                          .trim()}
                        fullWidth
                        error={!!errors.secondaryTitle}
                        helperText={errors.secondaryTitle?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={3}
                      className="grid"
                      sx={{
                        gridTemplateColumns: checkPrivileges(
                          "ROLE_SERVICEGROUP_CREATE",
                        )
                          ? "85% 10%"
                          : "100%",
                        "& .addNew path": { fill: themeColors?.primary },
                      }}
                    >
                      {watch("type")?.value === 102 ? null : (
                        <>
                          <Controller
                            name="serviceGroupId"
                            control={control}
                            defaultValue={null}
                            rules={{ required: false }}
                            render={({ field }) => (
                              <DynamicSelect
                                label={t("serviceGroup")}
                                {...field}
                                options={serviceGroups}
                                isSearchable
                                isClearable
                                defaultPlaceholder="phSelectServiceGroup"
                                onChange={(selectedOption: any) =>
                                  field.onChange(selectedOption)
                                }
                                error={errors.serviceGroupId?.message as string}
                                isLoading={searchLoading === "serviceGroups"}
                                onInputChange={(
                                  search: string,
                                  { action }: { action: string },
                                ) => {
                                  if (
                                    action === "input-change" ||
                                    (action !== "set-value" &&
                                      action !== "menu-close" &&
                                      action !== "input-blur")
                                  )
                                    handleDropdownActions(
                                      "serviceGroups",
                                      "searchIt",
                                      search,
                                    );
                                  else if (
                                    action === "input-blur" ||
                                    action === "menu-close"
                                  ) {
                                    if (dropdownSearchTerm || pageNo > 1)
                                      handleDropdownActions(
                                        "serviceGroups",
                                        "searchIt",
                                        "",
                                      );
                                    else setDropdownSearchTerm("");
                                    setPageNo(1);
                                  }
                                }}
                                onMenuScrollToBottom={() => {
                                  if (
                                    !(serviceGroupsData?.pages[0]?.data as any)
                                      .last
                                  )
                                    handleDropdownActions(
                                      "serviceGroups",
                                      "loadNext",
                                    );
                                }}
                                page={pageNo}
                              />
                            )}
                          />
                          {checkPrivileges("ROLE_SERVICEGROUP_CREATE") && (
                            <Tooltip
                              onClick={() => setAddNew(true)}
                              title={t("addServiceGroup")}
                            >
                              <ModalBtn className="mt-5 mx-2 mb-2 cursor-pointer h-[38px] addNew" />
                            </Tooltip>
                          )}
                          {addNew && (
                            <FormDialog
                              open={addNew}
                              onClose={(event, reason) =>
                                event && reason !== "backdropClick"
                                  ? setAddNew(false)
                                  : ""
                              }
                              title={t("addServiceGroup")}
                              createPopup={true}
                              fullWidth
                              hideActions={true}
                              content={
                                <ServiceGroupModal
                                  setAddNew={setAddNew}
                                  refetch={serviceGroupsRefetch}
                                />
                              }
                            />
                          )}
                        </>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="itemGroup"
                        control={control}
                        defaultValue={null}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("partType") + " *"}
                            description={
                              <Box>
                                <Box sx={{ marginBottom: "6px" }}>
                                  {t("partTypeInfoIntro", {
                                    item: t("service"),
                                  })}
                                </Box>
                                <Box component="ul" sx={{ margin: 0 }}>
                                  <li>
                                    <strong>{t("open")}:</strong>{" "}
                                    {t("partTypeInfoOpen")}
                                  </li>
                                  <li>
                                    <strong>{t("selected")}:</strong>{" "}
                                    {t("partTypeInfoSelected")}
                                  </li>
                                  <li>
                                    <strong>{t("groups")}:</strong>{" "}
                                    {t("partTypeInfoGroup")}
                                  </li>
                                  <li>
                                    <strong>{t("bundle")}:</strong>{" "}
                                    {t(
                                      "Automatically adds the selected parts when the service is added.",
                                    )}
                                  </li>
                                </Box>
                              </Box>
                            }
                            {...field}
                            options={groupOptions}
                            isSearchable
                            isClearable
                            defaultPlaceholder={t(
                              "selectOpenGroupSelectBundle",
                            )}
                            onChange={(selectedOption: any) => {
                              field.onChange(selectedOption);
                              setValue("itemGroupId", null);
                            }}
                            error={errors.itemGroup?.message as string}
                          />
                        )}
                      />
                    </Grid>
                    {watch("itemGroup")?.value === 1 ? (
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="itemGroupId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("itemGroup") + " *"}
                              {...field}
                              options={itemGroups}
                              isSearchable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={errors.itemGroupId?.message as string}
                              isLoading={searchLoading === "itemGroups"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "itemGroups",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "itemGroups",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (
                                  !(itemGroupsData?.pages[0]?.data as any).last
                                )
                                  handleDropdownActions(
                                    "itemGroups",
                                    "loadNext",
                                  );
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                    ) : null}
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="checklistMandatory"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <CommonCheckbox
                            {...field}
                            label={t("Is checklist mandatory?")}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => field.onChange(event.target.checked)}
                            checked={field.value}
                            color={themeColors?.primary}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="checklistId"
                        control={control}
                        defaultValue={null}
                        rules={{
                          required: watch("checklistMandatory")
                            ? t("This field is required.")
                            : false,
                        }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={
                              t("Checklist") +
                              (watch("checklistMandatory") ? " *" : "")
                            }
                            {...field}
                            options={checklists}
                            isSearchable
                            isClearable
                            defaultPlaceholder="phSelectChecklist"
                            onChange={(selectedOption: any) =>
                              field.onChange(selectedOption)
                            }
                            error={errors.checklistId?.message as string}
                            isLoading={searchLoading === "checklists"}
                            onInputChange={(
                              search: string,
                              { action }: { action: string },
                            ) => {
                              if (
                                action === "input-change" ||
                                (action !== "set-value" &&
                                  action !== "menu-close" &&
                                  action !== "input-blur")
                              )
                                handleDropdownActions(
                                  "checklists",
                                  "searchIt",
                                  search,
                                );
                              else if (
                                action === "input-blur" ||
                                action === "menu-close"
                              ) {
                                if (dropdownSearchTerm || pageNo > 1)
                                  handleDropdownActions(
                                    "checklists",
                                    "searchIt",
                                    "",
                                  );
                                else setDropdownSearchTerm("");
                                setPageNo(1);
                              }
                            }}
                            onMenuScrollToBottom={() => {
                              if (!(checklistsData?.pages[0]?.data as any).last)
                                handleDropdownActions("checklists", "loadNext");
                            }}
                            page={pageNo}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="applyToAllStations"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <CommonCheckbox
                            {...field}
                            label={t("applyToAllStations")}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => field.onChange(event.target.checked)}
                            checked={field.value}
                            color={themeColors?.primary}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="taxable"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <CommonCheckbox
                            {...field}
                            label={t("taxable")}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              field.onChange(event.target.checked);
                              if (!event.target.checked) {
                                setValue("taxProcedureId", null);
                              }
                              if (!params?.id && event.target.checked)
                                getDefaultTaxForPrice();
                            }}
                            checked={field.value}
                            color={themeColors?.primary}
                          />
                        )}
                      />
                    </Grid>
                    {watch("taxable") ? (
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="taxProcedureId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("taxProcedure") + " *"}
                              {...field}
                              options={taxProcedures}
                              isSearchable
                              onChange={(selectedOption: any) => {
                                field.onChange(selectedOption);
                                setValue(
                                  "taxValue",
                                  (
                                    watch("price") *
                                    (watch("taxProcedureId")?.percentageValue /
                                      100)
                                  )?.toFixed(2),
                                );
                                setValue(
                                  "priceWithTax",
                                  parseFloat(watch("price")) +
                                    watch("price") *
                                      (selectedOption.percentageValue / 100),
                                );
                              }}
                              error={errors.taxProcedureId?.message as string}
                              isLoading={searchLoading === "taxProcedures"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "taxProcedures",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "taxProcedures",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (
                                  !(taxProceduresData?.pages[0]?.data as any)
                                    .last
                                )
                                  handleDropdownActions(
                                    "taxProcedures",
                                    "loadNext",
                                  );
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                    ) : null}
                  </Grid>
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12}>
                      <Typography className="flex font-Saira font-[800] text-[19px] text-[#000000]">
                        {t("price")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography className="flex font-Saira font-[800] text-[19px] text-[#000000]">
                        {t(defaultPriceList || "")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="number"
                        {...register("price", {
                          required: t("This field is required."),
                        })}
                        onChange={(e: any) => {
                          const raw = e.target.value;
                          const parsed = parseFloat(raw);
                          const normalized =
                            raw !== "" &&
                            raw !== "." &&
                            !Number.isNaN(parsed) &&
                            parsed < 0
                              ? "0"
                              : raw;
                          setValue("price", normalized, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          const amount =
                            normalized === "" || normalized === "."
                              ? 0
                              : parseFloat(normalized) || 0;
                          setValue(
                            "priceWithTax",
                            (
                              amount +
                              (amount *
                                parseFloat(
                                  watch("taxProcedureId")?.percentageValue || 0,
                                )) /
                                100
                            )?.toFixed(2),
                          );
                          setValue(
                            "taxValue",
                            (
                              amount *
                              (watch("taxProcedureId")?.percentageValue / 100)
                            )?.toFixed(2),
                          );
                          if (!IsBtnEnabled) setIsBtnEnabled(true);
                        }}
                        label={
                          t("price") +
                          (loggedInUserCurrency
                            ? " (" + currencyLabel + ")"
                            : "") +
                          " *"
                        }
                        placeholder={t("phServicePrice")}
                        title={
                          t("price") +
                          (loggedInUserCurrency
                            ? " (" + currencyLabel + ")"
                            : "")
                        }
                        fullWidth
                        error={!!errors.price}
                        helperText={errors.price?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    {watch("taxable") && watch("taxProcedureId")?.value ? (
                      <>
                        <Grid item xs={12} md={3}>
                          <TextField
                            type="number"
                            {...register("tax")}
                            label={t("tax") + " (%)"}
                            placeholder={t("tax") + " (%)"}
                            title={t("tax") + " (%)"}
                            fullWidth
                            error={!!errors.tax}
                            helperText={errors.tax?.message as string}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <span className="font-Saira text-[13px]">
                                    {(watch("taxValue") || 0) +
                                      (loggedInUserCurrency
                                        ? " " + currencyLabel
                                        : "")}
                                  </span>
                                </InputAdornment>
                              ),
                            }}
                            value={
                              watch("taxProcedureId")?.percentageValue || 0
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            type="number"
                            {...register("priceWithTax")}
                            label={
                              t("priceWithTax") +
                              (loggedInUserCurrency
                                ? " (" + currencyLabel + ")"
                                : "")
                            }
                            placeholder={
                              t("priceWithTax") +
                              (loggedInUserCurrency
                                ? " (" + currencyLabel + ")"
                                : "")
                            }
                            title={
                              t("priceWithTax") +
                              (loggedInUserCurrency
                                ? " (" + currencyLabel + ")"
                                : "")
                            }
                            fullWidth
                            error={!!errors.priceWithTax}
                            helperText={errors.priceWithTax?.message as string}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e: any) => {
                              const value = parseFloat(e.target.value);
                              e.target.value = value < 0 ? 0 : value || 0;
                              const price =
                                parseFloat(e.target.value) /
                                (1 +
                                  (watch("taxProcedureId")?.percentageValue ||
                                    0) /
                                    100);
                              setValue(
                                "price",
                                price - Math.floor(price) > 0.9
                                  ? Math.round(price)
                                  : price?.toFixed(2),
                              );
                              setValue(
                                "taxValue",
                                (
                                  watch("price") *
                                  (watch("taxProcedureId")?.percentageValue /
                                    100)
                                )?.toFixed(2),
                              );
                              if (!IsBtnEnabled) setIsBtnEnabled(true);
                            }}
                          />
                        </Grid>
                      </>
                    ) : null}
                  </Grid>
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="pricingScope"
                        control={control}
                        defaultValue={null}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("pricingScope") + " *"}
                            description={
                              <Box>
                                <Box sx={{ marginBottom: "6px" }}>
                                  {t("pricingScopeInfoIntro")}
                                </Box>
                                <Box component="ul" sx={{ margin: 0 }}>
                                  <li>
                                    <strong>{t("Part & Service")}:</strong>{" "}
                                    {t("pricingScopeInfoPartsAndService")}
                                  </li>
                                  <li>
                                    <strong>{t("Service only")}:</strong>{" "}
                                    {t("pricingScopeInfoServiceOnly")}
                                  </li>
                                </Box>
                              </Box>
                            }
                            {...field}
                            options={pricingScopes}
                            isSearchable
                            isClearable
                            defaultPlaceholder="phSelectPricingScope"
                            onChange={(selectedOption: any) =>
                              field.onChange(selectedOption)
                            }
                            error={errors.pricingScope?.message as string}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="pricingType"
                        control={control}
                        defaultValue={null}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("pricingType") + " *"}
                            description={
                              <Box>
                                <Box sx={{ marginBottom: "6px" }}>
                                  {t("pricingTypeInfoIntro")}
                                </Box>
                                <Box component="ul" sx={{ margin: 0 }}>
                                  <li>
                                    <strong>{t("fixedPrice")}:</strong>{" "}
                                    {t("pricingTypeInfoFixedPrice")}
                                  </li>
                                  <li>
                                    <strong>{t("standardLaborCost")}:</strong>{" "}
                                    {t("pricingTypeInfoSLC")}
                                  </li>
                                </Box>
                              </Box>
                            }
                            {...field}
                            options={pricingTypes}
                            isSearchable
                            isClearable
                            defaultPlaceholder="phSelectPricingType"
                            onChange={(selectedOption: any) =>
                              field.onChange(selectedOption)
                            }
                            error={errors.pricingType?.message as string}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="attributes.serviceTimeUnit"
                        control={control}
                        defaultValue={serviceTimeUnitOptions[1]}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("timeUnit") + " *"}
                            {...field}
                            options={serviceTimeUnitOptions}
                            isSearchable={false}
                            isClearable={false}
                            onChange={(selectedOption: any) => {
                              const prevUnit =
                                (field.value as any)?.value ?? field.value ?? 2;
                              const newUnit = selectedOption?.value ?? 2;
                              const currentServiceTime = watch("serviceTime");
                              if (
                                currentServiceTime !== null &&
                                currentServiceTime !== undefined &&
                                currentServiceTime !== "" &&
                                prevUnit !== newUnit
                              ) {
                                const numericTime = +currentServiceTime;
                                if (!Number.isNaN(numericTime)) {
                                  const convertedTime =
                                    prevUnit === 1 && newUnit === 2
                                      ? numericTime * 60
                                      : prevUnit === 2 && newUnit === 1
                                        ? numericTime / 60
                                        : numericTime;
                                  setValue(
                                    "serviceTime",
                                    formatBilledTimeValue(convertedTime),
                                    { shouldDirty: true },
                                  );
                                }
                              }
                              field.onChange(selectedOption);
                              if (!IsBtnEnabled) setIsBtnEnabled(true);
                            }}
                            error={
                              (errors as any)?.attributes?.serviceTimeUnit
                                ?.message as string
                            }
                            description={
                              <Box>
                                <Box>{t("billedTimeUnitInfo")}</Box>
                              </Box>
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="number"
                        {...register("serviceTime", {
                          required: t("This field is required."),
                          validate: (value) =>
                            value > 0 || t("amountGreaterThanZero"),
                        })}
                        label={
                          (((watch("attributes.serviceTimeUnit") as any)
                            ?.value ??
                            watch("attributes.serviceTimeUnit") ??
                            2) === 1
                            ? t("billedTimeHours")
                            : t("billedTimeMinutes")) + " *"
                        }
                        placeholder={
                          ((watch("attributes.serviceTimeUnit") as any)
                            ?.value ??
                            watch("attributes.serviceTimeUnit") ??
                            2) === 1
                            ? t("phBilledTimeHours")
                            : t("phBilledTimeMinutes")
                        }
                        title={
                          ((watch("attributes.serviceTimeUnit") as any)
                            ?.value ??
                            watch("attributes.serviceTimeUnit") ??
                            2) === 1
                            ? t("billedTimeHours")
                            : t("billedTimeMinutes")
                        }
                        onBlur={(e) => {
                          const numericTime = +e.target.value;
                          if (
                            e.target.value !== "" &&
                            !Number.isNaN(numericTime)
                          ) {
                            setValue(
                              "serviceTime",
                              formatBilledTimeValue(numericTime),
                              { shouldDirty: true },
                            );
                          }
                        }}
                        fullWidth
                        error={!!errors.serviceTime}
                        helperText={errors.serviceTime?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="number"
                        {...register("minLabourPrice", {
                          validate: (value) => {
                            const price = parseFloat(watch("price") || 0);
                            if (!value) return true; // allow empty
                            return (
                              parseFloat(value) < price ||
                              t("minLabourLessThanPrice")
                            );
                          },
                        })}
                        onChange={(e: any) => {
                          const value = e.target.value;
                          e.target.value = value < 0 ? 0 : value || "";
                          if (!IsBtnEnabled) setIsBtnEnabled(true);
                        }}
                        label={
                          <Box className="flex items-center gap-1">
                            {t("minLabourPrice") +
                              (loggedInUserCurrency
                                ? " (" + currencyLabel + ")"
                                : "")}
                            <Tooltip
                              title={t("minLabourPriceInfo")}
                              enterDelay={0}
                              placement="top"
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: "#fff",
                                    boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
                                    fontFamily: "Saira, sans-serif",
                                    color: "#111827",
                                    fontSize: '11px',
                                    fontWeight: "400",
                                    lineHeight: 1.5,
                                    padding: "10px",
                                  },
                                },
                              }}
                            >
                              <InfoIcon
                                sx={{
                                  fontSize: 17,
                                  color: "#6b7280",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          </Box>
                        }
                        placeholder={t("phMinLaborPrice")}
                        title={
                          t("minLabourPrice") +
                          (loggedInUserCurrency
                            ? " (" + currencyLabel + ")"
                            : "")
                        }
                        fullWidth
                        error={!!errors.minLabourPrice}
                        helperText={errors.minLabourPrice?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="number"
                        {...register("itemLimit", {
                          required: false,
                          validate: (value) =>
                            value === null ||
                            value === undefined ||
                            value === "" ||
                            Number(value) > 0
                              ? true
                              : t("amountGreaterThanZero"),
                        })}
                        label={
                          <Box className="flex items-center gap-1">
                            {t("itemLimit")}
                            <Tooltip
                              title={t("itemLimitPlaceholder", {
                                item: t("service"),
                              })}
                              enterDelay={0}
                              placement="top"
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: "#fff",
                                    boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
                                    fontFamily: "Saira, sans-serif",
                                    color: "#111827",
                                    fontSize: '11px',
                                    fontWeight: "400",
                                    lineHeight: 1.5,
                                    padding: "10px",
                                  },
                                },
                              }}
                            >
                              <InfoIcon
                                sx={{
                                  fontSize: 17,
                                  color: "#6b7280",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          </Box>
                        }
                        placeholder={t("phServicePartsLimit")}
                        title={t("itemLimit")}
                        fullWidth
                        error={!!errors.itemLimit}
                        helperText={errors.itemLimit?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                  {watch("type")?.value === 102 ? (
                    <Grid container spacing={2} className="my-[0px] ml-[3px]">
                      <Grid item xs={12}>
                        <Typography className="flex font-Saira font-[800] text-[19px] text-[#000000]">
                          {t("cost")}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3} className="pt-0">
                        <Controller
                          name="taxableCost"
                          control={control}
                          defaultValue={false}
                          render={({ field }) => (
                            <CommonCheckbox
                              {...field}
                              label={t("isTaxableCost")}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                field.onChange(event.target.checked);
                                if (!event.target.checked) {
                                  setValue("costTaxProcedureId", null);
                                }
                                if (!params?.id && event.target.checked)
                                  getDefaultTaxForCost();
                              }}
                              checked={field.value}
                              color={themeColors?.primary}
                            />
                          )}
                        />
                      </Grid>
                      {watch("taxableCost") ? (
                        <Grid item xs={12} md={3} className="pt-0">
                          <Controller
                            name="costTaxProcedureId"
                            control={control}
                            defaultValue={null}
                            rules={{ required: t("This field is required.") }}
                            render={({ field }) => (
                              <DynamicSelect
                                label={t("taxProcedure") + " *"}
                                {...field}
                                options={taxProcedures}
                                isSearchable
                                onChange={(selectedOption: any) => {
                                  field.onChange(selectedOption);
                                  setValue(
                                    "costtaxValue",
                                    (
                                      watch("cost") *
                                      (watch("costTaxProcedureId")
                                        ?.percentageValue /
                                        100)
                                    )?.toFixed(2),
                                  );
                                  setValue(
                                    "costWithTax",
                                    parseFloat(watch("cost")) +
                                      watch("cost") *
                                        (selectedOption.percentageValue / 100),
                                  );
                                }}
                                error={
                                  errors.costTaxProcedureId?.message as string
                                }
                                isLoading={
                                  searchLoading === "costtaxProcedures"
                                }
                                onInputChange={(
                                  search: string,
                                  { action }: { action: string },
                                ) => {
                                  if (
                                    action === "input-change" ||
                                    (action !== "set-value" &&
                                      action !== "menu-close" &&
                                      action !== "input-blur")
                                  )
                                    handleDropdownActions(
                                      "costtaxProcedures",
                                      "searchIt",
                                      search,
                                    );
                                  else if (
                                    action === "input-blur" ||
                                    action === "menu-close"
                                  ) {
                                    if (dropdownSearchTerm || pageNo > 1)
                                      handleDropdownActions(
                                        "costtaxProcedures",
                                        "searchIt",
                                        "",
                                      );
                                    else setDropdownSearchTerm("");
                                    setPageNo(1);
                                  }
                                }}
                                onMenuScrollToBottom={() => {
                                  if (
                                    !(taxProceduresData?.pages[0]?.data as any)
                                      .last
                                  )
                                    handleDropdownActions(
                                      "costtaxProcedures",
                                      "loadNext",
                                    );
                                }}
                                page={pageNo}
                              />
                            )}
                          />
                        </Grid>
                      ) : null}
                      <Grid item xs={12} md={3} className="pt-0">
                        <TextField
                          type="number"
                          {...register("cost", {
                            required: t("This field is required."),
                          })}
                          onChange={(e: any) => {
                            const value = parseFloat(e.target.value);
                            e.target.value = value < 0 ? 0 : value || 0;
                            setValue(
                              "costWithTax",
                              (
                                parseFloat(e.target.value) +
                                (parseFloat(e.target.value) *
                                  parseFloat(
                                    watch("costTaxProcedureId")
                                      ?.percentageValue || 0,
                                  )) /
                                  100
                              )?.toFixed(2),
                            );
                            setValue(
                              "costtaxValue",
                              (
                                parseFloat(e.target.value) *
                                (watch("costTaxProcedureId")?.percentageValue /
                                  100)
                              )?.toFixed(2),
                            );
                            if (!IsBtnEnabled) setIsBtnEnabled(true);
                          }}
                          label={
                            t("cost") +
                            (loggedInUserCurrency
                              ? " (" + currencyLabel + ")"
                              : "") +
                            " *"
                          }
                          placeholder={
                            t("cost") +
                            (loggedInUserCurrency
                              ? " (" + currencyLabel + ")"
                              : "")
                          }
                          title={
                            t("cost") +
                            (loggedInUserCurrency
                              ? " (" + currencyLabel + ")"
                              : "")
                          }
                          fullWidth
                          error={!!errors.cost}
                          helperText={errors.cost?.message as string}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      {watch("taxableCost") &&
                      watch("costTaxProcedureId")?.value ? (
                        <>
                          <Grid item xs={12} md={3} className="pt-0">
                            <TextField
                              type="number"
                              {...register("costtax")}
                              label={t("tax") + " (%)"}
                              placeholder={t("tax") + " (%)"}
                              title={t("tax") + " (%)"}
                              fullWidth
                              error={!!errors.costtax}
                              helperText={errors.costtax?.message as string}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                readOnly: true,
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <span className="font-Saira text-[13px]">
                                      {(watch("costtaxValue") || 0) +
                                        (loggedInUserCurrency
                                          ? " " + currencyLabel
                                          : "")}
                                    </span>
                                  </InputAdornment>
                                ),
                              }}
                              value={
                                watch("costTaxProcedureId")?.percentageValue ||
                                0
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={3} className="pt-0">
                            <TextField
                              type="number"
                              {...register("costWithTax")}
                              label={
                                t("costWithTax") +
                                (loggedInUserCurrency
                                  ? " (" + currencyLabel + ")"
                                  : "")
                              }
                              placeholder={
                                t("costWithTax") +
                                (loggedInUserCurrency
                                  ? " (" + currencyLabel + ")"
                                  : "")
                              }
                              title={
                                t("costWithTax") +
                                (loggedInUserCurrency
                                  ? " (" + currencyLabel + ")"
                                  : "")
                              }
                              fullWidth
                              error={!!errors.costWithTax}
                              helperText={errors.costWithTax?.message as string}
                              InputLabelProps={{ shrink: true }}
                              onChange={(e: any) => {
                                const value = parseFloat(e.target.value);
                                e.target.value = value < 0 ? 0 : value || 0;
                                const cost =
                                  parseFloat(e.target.value) /
                                  (1 +
                                    (watch("costTaxProcedureId")
                                      ?.percentageValue || 0) /
                                      100);
                                setValue(
                                  "cost",
                                  cost - Math.floor(cost) > 0.9
                                    ? Math.round(cost)
                                    : cost?.toFixed(2),
                                );
                                setValue(
                                  "costtaxValue",
                                  (
                                    watch("cost") *
                                    (watch("costTaxProcedureId")
                                      ?.percentageValue /
                                      100)
                                  )?.toFixed(2),
                                );
                                if (!IsBtnEnabled) setIsBtnEnabled(true);
                              }}
                            />
                          </Grid>
                        </>
                      ) : null}
                    </Grid>
                  ) : null}
                  <Grid container spacing={2} className="my-[0px] ml-[3px]">
                    <Grid item xs={12}>
                      <Typography className="flex font-Saira font-[800] text-[19px] text-[#000000]">
                        {t("frequency")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="number"
                        {...register("serviceEveryMiles")}
                        onChange={(e: any) => {
                          const value = e.target.value;
                          e.target.value = value < 0 ? 0 : value || "";
                          if (!IsBtnEnabled) setIsBtnEnabled(true);
                        }}
                        label={t("Mileage (km)")}
                        placeholder={t("phServiceMileage")}
                        title={t("Mileage (km)")}
                        fullWidth
                        error={!!errors.serviceEveryMiles}
                        helperText={errors.serviceEveryMiles?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="number"
                        {...register("serviceEveryPeriodTime")}
                        onChange={(e: any) => {
                          const value = e.target.value;
                          e.target.value =
                            value < 0 ? 0 : parseInt(value) || "";
                          if (!IsBtnEnabled) setIsBtnEnabled(true);
                        }}
                        label={t("Number of Days")}
                        placeholder={t("phServiceDays")}
                        title={t("Number of Days")}
                        fullWidth
                        error={!!errors.serviceEveryPeriodTime}
                        helperText={
                          errors.serviceEveryPeriodTime?.message as string
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </>
            }
          />
        )}
      </div>
    </>
  );
}
